import { batch, signal, type ReadonlySignal, type Signal } from '@preact/signals'
import type { PageOwner } from '../shell/owner.ts'

/** One page timer; only eligible, actually visible rows receive its publications. */
export function createPageClock(owner: PageOwner) {
  const rows = new Map<string, { now: Signal<number>; readers: number }>()
  const eligible = new Map<string, number>()
  let timer: ReturnType<typeof setInterval> | undefined, disposed = false
  const item = (ref: string): ReadonlySignal<number> => {
    let row = rows.get(ref)
    if (!row) rows.set(ref, row = { now: signal(Date.now()), readers: 0 })
    return row.now
  }
  const publish = () => {
    const now = Date.now()
    batch(() => { for (const ref of eligible.keys()) {
      const cell = rows.get(ref)!.now
      if (Math.floor(cell.peek() / 1000) !== Math.floor(now / 1000)) cell.value = now
    } })
  }
  const sync = () => {
    const active = !disposed && owner.active && !owner.disposed && !document.hidden && eligible.size > 0
    if (!active && timer !== undefined) { clearInterval(timer); timer = undefined }
    else if (active && timer === undefined) { publish(); timer = setInterval(publish, 1000) }
  }
  const activity = owner.onActive(sync)
  document.addEventListener('visibilitychange', sync)
  const dispose = owner.own(() => {
    if (disposed) return
    disposed = true; activity(); document.removeEventListener('visibilitychange', sync)
    if (timer !== undefined) clearInterval(timer)
    timer = undefined; eligible.clear(); rows.clear()
  })
  return {
    item,
    /** Call only while onscreen and running. A missing native start never registers. */
    watch(ref: string, since: number | undefined): () => void {
      if (disposed || !Number.isFinite(since)) return () => {}
      item(ref); const row = rows.get(ref)!
      const now = Date.now()
      if (owner.active && !document.hidden && Math.floor(row.now.peek() / 1000) !== Math.floor(now / 1000)) row.now.value = now
      row.readers++; eligible.set(ref, (eligible.get(ref) ?? 0) + 1); sync()
      let live = true
      return () => {
        if (!live) return
        live = false; row.readers--
        const count = eligible.get(ref) ?? 0
        if (count <= 1) eligible.delete(ref); else eligible.set(ref, count - 1)
        sync()
      }
    },
    /** A row without a timer still releases its lazily created cell when it unmounts. */
    forget(ref: string) { const row = rows.get(ref); if (row && !row.readers) rows.delete(ref) },
    get eligible() { return eligible.size },
    get ticking() { return timer !== undefined },
    dispose,
  }
}
export type PageClock = ReturnType<typeof createPageClock>
